import { Injectable } from '@angular/core';
import { Environment } from '../../../../environments/environment';
import {
    accountKey,
    assistantKey,
    isAdapterKey,
    orgKey,
    parseKey,
} from './selection-storage.keys';

type WriteFailureListener = (err: unknown) => void;

/**
 * Adapter de persistência das seleções correntes (organização, conta,
 * assistente) em `sessionStorage`.
 *
 * Garantias:
 * - Side-effect-safe: nenhum método propaga exceção do storage.
 * - Sem userId (logout / JWT inválido) → reads viram `null`, writes viram
 *   no-op silencioso (warning único em dev; notificação única via
 *   `onWriteFailure`).
 * - Métodos de varredura nunca tocam chaves alheias ao próprio adapter
 *   (gating via `isAdapterKey`).
 */
@Injectable({ providedIn: 'root' })
export class SelectionStorageAdapter {
    private userId: string | null = null;
    private warnedNotReady = false;
    private writeFailureNotified = false;
    private writeFailureListener: WriteFailureListener | null = null;

    setUserId(userId: string | null): void {
        this.userId = userId;
        // Reset de sinalização por sessão: novo login pode ter outro userId.
        this.warnedNotReady = false;
        this.writeFailureNotified = false;
    }

    isReady(): boolean {
        return this.userId !== null;
    }

    // ─── Organização ─────────────────────────────────────────────────────

    getOrgId(): string | null {
        if (!this.userId) return this.readNotReady();
        return this.readSession(orgKey(this.userId));
    }

    setOrgId(orgId: string): void {
        if (!this.userId) {
            this.writeNotReady();
            return;
        }
        this.writeSession(orgKey(this.userId), orgId);
    }

    clearOrg(): void {
        if (!this.userId) return;
        this.removeSession(orgKey(this.userId));
    }

    // ─── Conta ───────────────────────────────────────────────────────────

    getAccountId(orgId: string): string | null {
        if (!this.userId) return this.readNotReady();
        return this.readSession(accountKey(this.userId, orgId));
    }

    setAccountId(orgId: string, accountId: string): void {
        if (!this.userId) {
            this.writeNotReady();
            return;
        }
        this.writeSession(accountKey(this.userId, orgId), accountId);
    }

    clearAccount(orgId: string): void {
        if (!this.userId) return;
        this.removeSession(accountKey(this.userId, orgId));
    }

    // ─── Assistente ──────────────────────────────────────────────────────

    getAssistantId(orgId: string, accountId: string): string | null {
        if (!this.userId) return this.readNotReady();
        return this.readSession(assistantKey(this.userId, orgId, accountId));
    }

    setAssistantId(orgId: string, accountId: string, assistantId: string): void {
        if (!this.userId) {
            this.writeNotReady();
            return;
        }
        this.writeSession(assistantKey(this.userId, orgId, accountId), assistantId);
    }

    clearAssistant(orgId: string, accountId: string): void {
        if (!this.userId) return;
        this.removeSession(assistantKey(this.userId, orgId, accountId));
    }

    // ─── Limpeza ─────────────────────────────────────────────────────────

    /**
     * Remove chaves de adapter pertencentes a OUTROS user_ids no
     * sessionStorage. Chaves do usuário corrente e chaves de terceiros
     * (não-adapter) são preservadas.
     */
    purgeOtherUsers(): void {
        if (!this.userId) return;
        const currentPrefix = `${this.userId}:`;
        this.forEachAdapterKey((key) => {
            if (!key.startsWith(currentPrefix)) {
                this.removeSession(key);
            }
        });
    }

    /**
     * Remove chaves de conta/assistente do usuário corrente que apontam
     * para orgs/contas que ele não tem mais acesso (lista vinda de `/me/*`).
     * Chaves de organização não são purgadas — `handleCurrentOrganization`
     * lida com seleções inválidas individualmente.
     */
    purgeOrphans(
        activeOrgIds: string[],
        activeAccountIdsByOrg: Record<string, string[]>,
    ): void {
        if (!this.userId) return;
        const orgSet = new Set(activeOrgIds);
        this.forEachAdapterKey((key) => {
            const parsed = parseKey(key);
            if (!parsed || parsed.userId !== this.userId) return;
            if (parsed.kind === 'account') {
                // Chave inativa se: (a) org não está mais ativa, ou
                // (b) o accountId armazenado não pertence à org ativa.
                if (!orgSet.has(parsed.orgId)) {
                    this.removeSession(key);
                    return;
                }
                const storedAccountId = this.readSession(key);
                const accIds = activeAccountIdsByOrg[parsed.orgId];
                if (!storedAccountId || !accIds || !accIds.includes(storedAccountId)) {
                    this.removeSession(key);
                }
            } else if (parsed.kind === 'assistant') {
                const accIds = activeAccountIdsByOrg[parsed.orgId];
                if (!accIds || !accIds.includes(parsed.accountId)) {
                    this.removeSession(key);
                }
            }
        });
    }

    /**
     * Remove todas as chaves do adapter pertencentes ao usuário corrente.
     * Usado em `signOut()` (defensivo — keycloak.logout normalmente já
     * causa unload da página).
     */
    clearAllForCurrentUser(): void {
        if (!this.userId) return;
        const currentPrefix = `${this.userId}:`;
        this.forEachAdapterKey((key) => {
            if (key.startsWith(currentPrefix)) this.removeSession(key);
        });
    }

    // ─── Observabilidade ─────────────────────────────────────────────────

    /**
     * Registra callback invocado UMA vez por sessão na primeira falha de
     * escrita. Subsequentes falhas são silenciosas para evitar ruído.
     */
    onWriteFailure(callback: WriteFailureListener): void {
        this.writeFailureListener = callback;
    }

    // ─── Internals ───────────────────────────────────────────────────────

    private readSession(key: string): string | null {
        try {
            return sessionStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private writeSession(key: string, value: string): void {
        try {
            sessionStorage.setItem(key, value);
        } catch (err) {
            this.notifyWriteFailure(err);
        }
    }

    private removeSession(key: string): void {
        try {
            sessionStorage.removeItem(key);
        } catch {
            // remoção sem impacto observável
        }
    }

    private forEachAdapterKey(fn: (key: string) => void): void {
        let keys: string[];
        try {
            keys = Object.keys(sessionStorage);
        } catch {
            return;
        }
        for (const key of keys) {
            if (isAdapterKey(key)) fn(key);
        }
    }

    private readNotReady(): null {
        this.warnNotReadyOnce();
        return null;
    }

    private writeNotReady(): void {
        this.warnNotReadyOnce();
        this.notifyWriteFailure(new Error('SelectionStorageAdapter: userId não definido'));
    }

    private warnNotReadyOnce(): void {
        if (this.warnedNotReady || Environment.production) return;
        this.warnedNotReady = true;
        // eslint-disable-next-line no-console
        console.warn(
            '[SelectionStorageAdapter] acesso antes de setUserId — operação ignorada.',
        );
    }

    private notifyWriteFailure(err: unknown): void {
        if (this.writeFailureNotified || !this.writeFailureListener) return;
        this.writeFailureNotified = true;
        try {
            this.writeFailureListener(err);
        } catch {
            // Listener não deve quebrar o adapter.
        }
    }
}
